import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactElement } from 'react';

import { ErrorState, SkeletonRows } from '@/components/admin/states';
import { Notice } from '@/components/admin/Notice';
import { ROLE_LABELS, type Role } from '@/features/auth/api';
import { fetchEventStaff, setEventStaff } from '@/features/events/api';
import { fetchStaff, staffLabel } from '@/features/staff/api';
import { ApiError } from '@/lib/api/http';

/**
 * イベントの担当者（理事・講師／サクセス担当）。
 * 担任制をやめたため、ここで指定した人だけが、この回の参加者のプロフィール・出欠・
 * 提出物を見られるようになる（D-115）。席次はシステムでは扱わない（D-284）。
 * 1対1の面談は相手ロール1人だけ（D-236）。通常イベントは複数可（D-220）。
 */
export function EventStaffTab({
  eventId,
  counterpartRole = null,
}: {
  eventId: string;
  /** 種別が1対1の面談なら相手のロール。null は通常イベント */
  counterpartRole?: Role | null;
}): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['event-staff', eventId],
    queryFn: () => fetchEventStaff(eventId),
  });
  const candidates = useQuery({
    queryKey: ['staff', 'all'],
    queryFn: () => fetchStaff({ includeInactive: false }),
  });

  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.data) setSelected(query.data.staff.map((s) => s.user_id));
  }, [query.data]);

  if (query.isPending) return <SkeletonRows rows={3} />;
  if (query.isError) {
    return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  }

  function toggle(userId: string, checked: boolean): void {
    setSelected((prev) => (checked ? [...prev, userId] : prev.filter((id) => id !== userId)));
  }

  async function onSave(): Promise<void> {
    if (counterpartRole !== null && selected.length !== 1) {
      setError(`${ROLE_LABELS[counterpartRole]}を1人選んでください。`);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await setEventStaff(eventId, selected);
      await queryClient.invalidateQueries({ queryKey: ['event-staff', eventId] });
      setNotice('担当者を保存しました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  const rolesToShow =
    counterpartRole !== null ? ([counterpartRole] as const) : (['director', 'success'] as const);

  return (
    <>
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {notice && <Notice kind="success">{notice}</Notice>}

      <p className="mb-3 text-wptext-muted">
        {counterpartRole !== null ? (
          <>
            この面談の相手は<strong>{ROLE_LABELS[counterpartRole]}1人</strong>です。変更できます。
            {counterpartRole === 'success' ? '理事・講師' : 'サクセス担当'}は選べません。
          </>
        ) : (
          <>
            この回を担当する理事・講師とサクセス担当を選びます。
            <strong>ここで選んだ人だけ</strong>
            が、この回の参加者のプロフィール・出欠・提出物を見られます。
            会員ごとに固定の担当を決める方式はとっていないため、毎回ここで指定してください。
          </>
        )}
      </p>

      {candidates.isError && (
        <Notice kind="error" dismissible={false}>
          担当者の候補を取得できませんでした。
        </Notice>
      )}

      {rolesToShow.map((role) => {
        const group = (candidates.data ?? []).filter((s) => s.role === role);
        if (group.length === 0) return null;
        return (
          <div key={role} className="mb-4">
            <p className="mb-1 font-semibold text-wptext-strong">{ROLE_LABELS[role]}</p>
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              {group.map((staff) => (
                <label key={staff.user_id} className="flex items-center gap-2 py-1">
                  <input
                    type={counterpartRole !== null ? 'radio' : 'checkbox'}
                    name={counterpartRole !== null ? 'event-staff-counterpart' : undefined}
                    checked={selected.includes(staff.user_id)}
                    onChange={(e) => {
                      if (counterpartRole !== null) setSelected([staff.user_id]);
                      else toggle(staff.user_id, e.target.checked);
                    }}
                  />
                  <span>{staffLabel(staff)}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        disabled={busy}
        onClick={() => void onSave()}
        className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
      >
        {busy ? '保存中…' : '担当者を保存'}
      </button>
    </>
  );
}
